import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin, Phone, Clock, Calendar } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const INSTAGRAM_URL = "https://www.instagram.com/beauty_by_kuleva";
const FACEBOOK_URL = "https://www.facebook.com/share/1BvBZyGQ9s/?mibextid=wwXIfr";
const TIMMA_URL = "https://bestill.timma.no/beautybykuleva";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-oasis-20/artifacts/8w9qtp5u_logo1.jpg";

export const Footer = () => {
  const { t, language } = useLanguage();
  const langPrefix = `/${language}`;
  
  return (
    <footer className="bg-[#F9F5F6] border-t border-[#E8D0D0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand with Full Logo */}
          <div className="space-y-4">
            <Link to={langPrefix} className="inline-block">
              <img 
                src={LOGO_URL}
                alt="Marina Kuleva Beauty Studio"
                className="h-24 w-auto object-contain"
              />
            </Link>
            <p className="text-[#8C8C8C] text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="flex space-x-4">
              <a 
                href={INSTAGRAM_URL}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#D4A5A5] hover:bg-[#D4A5A5] hover:text-white transition-colors shadow-soft"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href={FACEBOOK_URL}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#D4A5A5] hover:bg-[#D4A5A5] hover:text-white transition-colors shadow-soft"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="mailto:post@beautybykuleva.no"
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#D4A5A5] hover:bg-[#D4A5A5] hover:text-white transition-colors shadow-soft"
                aria-label="Email"
                data-testid="footer-email"
              >
                <Mail size={18} />
              </a>
            </div>
            {/* Book Now Button */}
            <a
              href={TIMMA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 btn-primary text-sm mt-2"
              data-testid="footer-book-btn"
            >
              <Calendar size={16} />
              {t("nav.bookNow")}
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#4A4A4A] mb-6">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to={`${langPrefix}/services`}
                  className="text-[#8C8C8C] hover:text-[#D4A5A5] transition-colors text-sm"
                >
                  {t("footer.links.services")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${langPrefix}/booking`}
                  className="text-[#8C8C8C] hover:text-[#D4A5A5] transition-colors text-sm"
                >
                  {t("footer.links.bookAppointment")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${langPrefix}/gallery`}
                  className="text-[#8C8C8C] hover:text-[#D4A5A5] transition-colors text-sm"
                >
                  {t("footer.links.gallery")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${langPrefix}/contact`}
                  className="text-[#8C8C8C] hover:text-[#D4A5A5] transition-colors text-sm"
                >
                  {t("footer.links.contact")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#4A4A4A] mb-6">
              {t("footer.contactUs")}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-[#D4A5A5] mt-0.5 flex-shrink-0" />
                <span className="text-[#8C8C8C] text-sm">
                  Odins gate 13B<br />
                  0266 Oslo
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={18} className="text-[#D4A5A5] flex-shrink-0" />
                <a href="tel:+4793046396" className="text-[#8C8C8C] text-sm hover:text-[#D4A5A5]">+47 930 46 396</a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={18} className="text-[#D4A5A5] flex-shrink-0" />
                <a href="mailto:post@beautybykuleva.no" className="text-[#8C8C8C] text-sm hover:text-[#D4A5A5]">post@beautybykuleva.no</a>
              </li>
              <li className="flex items-start space-x-3">
                <Clock size={18} className="text-[#D4A5A5] mt-0.5 flex-shrink-0" />
                <span className="text-[#8C8C8C] text-sm">
                  Man–Lør: 09:30–18:00<br />
                  Søndag: Etter avtale
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#E8D0D0] mt-12 pt-8 text-center">
          <p className="text-[#8C8C8C] text-sm">
            &copy; {new Date().getFullYear()} Beauty by Kuleva. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
};
